import { AlertTriangle } from 'lucide-react'
import { Link } from 'react-router-dom'

import { DueDateBadge, StatusPill, UserAvatar } from '@/shared/components/common'
import { Switch } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { ProjectDetail } from '@/shared/types/domain'

import { DueDatePicker } from '@/features/tasks/components'

import { StagePicker } from './StagePicker'

export interface ProjectStatusBarProps {
  project: ProjectDetail
  onStageChange: (stageId: string) => void
  onVisibilityChange: (visible: boolean) => void
  onTargetEndChange: (date: string | null) => void
  readOnly?: boolean
  className?: string
}

/**
 * Una celda de la barra: rótulo arriba, valor abajo.
 *
 * El valor va en una banda de **altura fija**. Sin eso, cada celda medía lo que
 * medía su contenido —un pill de 24px, un avatar de 28, una línea de texto de
 * 18— y como la fila se alineaba por abajo, los rótulos terminaban cada uno a
 * una altura distinta: la barra se leía torcida aunque cada celda estuviera
 * bien armada. Con la banda, los seis rótulos comparten línea y los seis
 * valores también.
 */
function Slot({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-2xs leading-none tracking-wide text-text-3 uppercase">{label}</span>
      <div className="flex h-7 min-w-0 items-center">{children}</div>
    </div>
  )
}

/**
 * La barra de estado es la SEGUNDA LECTURA de la pantalla: el nombre del
 * proyecto se reconoce por forma, lo que cambia dia a dia es esto.
 */
export function ProjectStatusBar({
  project,
  onStageChange,
  onVisibilityChange,
  onTargetEndChange,
  readOnly,
  className,
}: ProjectStatusBarProps) {
  // `null` mientras el detalle viaja: no se avisa de lo que todavía no se sabe.
  const visible = project.client_visible
  const vacioParaElCliente =
    visible !== null && visible.tasks + visible.documents + visible.components === 0

  const t = useT()

  return (
    <div className={cn('flex flex-wrap items-start gap-x-6 gap-y-3', className)}>
      <Slot label={t('common.label.phase')}>
        <StagePicker
          stage={project.stage}
          stages={project.stages}
          disabled={readOnly}
          onChange={(stage) => onStageChange(stage.id)}
        />
      </Slot>

      <Slot label={t('common.label.priority')}>
        {project.is_ongoing ? (
          <StatusPill size="md" tone="ongoing">
            {t('projects.ongoing')}
          </StatusPill>
        ) : (
          <span className="num text-base text-text-1">
            {t('projects.status.position', { position: project.position ?? 0 })}
          </span>
        )}
      </Slot>

      <Slot label={t('common.label.owner')}>
        <UserAvatar user={project.internal_owner} withName size="md" />
      </Slot>

      <Slot label={t('projects.status.clientContact')}>
        {project.client_contact ? (
          <a
            href={`mailto:${project.client_contact.email}`}
            className="text-xs text-text-1 hover:text-signal"
          >
            {project.client_contact.name}
          </a>
        ) : (
          <span className="text-xs text-text-3">{t('projects.status.noClientContact')}</span>
        )}
      </Slot>

      {/*
        La ENTREGA se edita acá, en la barra, sin pasar por el formulario.
        Es el campo que más se mueve de una ficha —una fecha que se corre es la
        conversación más frecuente con un cliente— y mandarla a un panel de seis
        campos para cambiar un día era el camino largo para lo más común.

        Los proyectos permanentes no tienen entrega: no se dibuja un control que
        no se puede usar.
      */}
      {project.is_ongoing ? null : (
        <Slot label={t('projects.summary.targetEnd')}>
          <DueDatePicker
            // El rótulo de arriba es un `<span>`, no un `<label for>`: sin
            // esto el input queda sin nombre accesible y un lector de
            // pantalla anuncia "fecha, editable" sin decir cuál.
            label={t('projects.summary.targetEnd')}
            value={project.target_end_date}
            disabled={readOnly}
            onChange={onTargetEndChange}
          />
        </Slot>
      )}

      {/* Derivado de las tareas, no editable: es la próxima que vence. */}
      <Slot label={t('projects.status.nextDue')}>
        <DueDateBadge date={project.next_due_date} />
      </Slot>

      <Slot label={t('common.label.company')}>
        <Link
          to={`/companies/${project.company.id}`}
          className="text-xs text-text-1 hover:text-signal"
        >
          {project.company.name}
        </Link>
      </Slot>

      <div className="ml-auto flex items-center gap-2">
        {/*
          El aviso que no existía: publicado y sin nada adentro.

          El toggle de visibilidad está por tarea y por documento, uno por uno, y
          nada decía el resultado. Del otro lado eso es un cliente que abre su
          proyecto y encuentra cuatro pestañas vacías (QA funcional 4.1). No es
          un error —publicar es un acto explícito y eso está bien— pero quien
          publica el proyecto merece enterarse de qué entregó.

          Sólo aparece cuando el proyecto ESTÁ publicado: si todavía no lo está,
          que no haya nada visible es lo esperado y avisarlo sería ruido.
        */}
        {project.is_client_visible && vacioParaElCliente ? (
          <span
            role="status"
            className="inline-flex items-center gap-1 text-2xs text-due-soon"
            title={t('projects.status.publishedEmptyHint')}
          >
            <AlertTriangle size={12} aria-hidden="true" />
            {t('projects.status.publishedEmpty')}
          </span>
        ) : null}
        <Switch
          label={t('projects.status.clientVisible')}
          checked={project.is_client_visible}
          disabled={readOnly}
          onChange={onVisibilityChange}
        />
      </div>
    </div>
  )
}
